import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { useBranding } from '../lib/branding.js';
import { api, ApiError } from '../lib/api.js';
import { PasswordInput } from '../components/ui.js';

/**
 * Lands here from an invite or forgot-password email
 * ({@see backend App\Support\SetPasswordUrl}) with `?token=&email=` in the
 * query string. Sets the password, then signs the user straight in.
 */
export function SetPasswordScreen(): ReactNode {
  const [params] = useSearchParams();
  const { login } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  const token = params.get('token') ?? '';
  const email = params.get('email') ?? '';

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  if (!token || !email) {
    return (
      <div className="auth-wrap">
        <div className="card card-pad auth-card stack">
          <h1 style={{ fontSize: 18 }}>{branding.productName}</h1>
          <div className="form-error">
            This link is missing its token — copy it directly from the invite email.
          </div>
        </div>
      </div>
    );
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await api.setPassword({ email, token, password, passwordConfirmation });
      await login(email, password);
      navigate('/displays', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.fieldMessage() ?? err.message) : 'Could not reach the server.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-wrap">
      <form className="card card-pad auth-card stack" onSubmit={submit}>
        <div>
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt={branding.productName}
              style={{ maxHeight: 40, maxWidth: '70%', marginBottom: 4 }}
            />
          ) : (
            <h1 style={{ fontSize: 18 }}>{branding.productName}</h1>
          )}
          <p className="muted small" style={{ margin: '4px 0 0' }}>
            Set a password for {email}
          </p>
        </div>
        <label className="field">
          New password
          <PasswordInput
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
            minLength={8}
            required
            autoFocus
          />
        </label>
        <label className="field">
          Confirm password
          <PasswordInput
            autoComplete="new-password"
            value={passwordConfirmation}
            onChange={setPasswordConfirmation}
            minLength={8}
            required
          />
        </label>
        {error ? (
          <div className="form-error">
            {error}{' '}
            <Link to={`/forgot-password?email=${encodeURIComponent(email)}`}>Send a new link</Link>
          </div>
        ) : null}
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? 'Setting password…' : 'Set password'}
        </button>
      </form>
    </div>
  );
}
